<template>
    <div class="text-grind">
        <div
            v-for="(productType, productTypeIndex) in productTypes"
            :key="productType.name"
        >
            <h2 class="mb-10">{{ singular(productType.name) }} Lead Times</h2>
            <div
                v-for="(group, groupIndex) in productType.groups"
                :key="group.name"
                class="mb-20"
            >
                <div class="mb-20 flex gap-7">
                    <div class="flex-0 hidden w-52 lg:block">
                        <img class="w-full" :src="group.image" />
                    </div>
                    <div class="flex-1">
                        <div class="mb-4">
                            <h3 class="mb-0 mr-5 inline-block">
                                {{ group.name }}
                            </h3>
                        </div>

                        <div v-if="group.note" v-html="group.note" />
                        <div
                            v-for="availability in group.availabilities"
                            :key="availability.name"
                            class="mt-12"
                        >
                            <div class="mb-3">
                                <span class="font-avenir-bold mr-2">{{
                                    availability.name
                                }}</span>
                                <a
                                    v-if="
                                        group.designLabLinkUrl &&
                                        availability.name ===
                                            'Roastar Design Lab'
                                    "
                                    :href="group.designLabLinkUrl"
                                    target="_blank"
                                    class="text-sm"
                                    >{{ group.designLabLinkText }}</a
                                >
                                <a
                                    v-if="
                                        group.stockLinkUrl &&
                                        availability.name === 'Stock / Blank'
                                    "
                                    :href="group.stockLinkUrl"
                                    target="_blank"
                                    class="text-sm"
                                    >{{ group.stockLinkText }}</a
                                >
                                <a
                                    v-if="
                                        group.customPrintedLinkUrl &&
                                        availability.name === 'Custom-printed'
                                    "
                                    :href="group.customPrintedLinkUrl"
                                    target="_blank"
                                    class="text-sm"
                                    >{{ group.customPrintedLinkText }}</a
                                >
                            </div>
                            <div
                                v-if="availability.note"
                                class="mb-6"
                                v-html="availability.note"
                            />
                            <table class="w-full table-auto rounded-xl shadow">
                                <thead>
                                    <tr class="border-b border-grind-800">
                                        <th
                                            class="font-avenir-medium px-6 py-4 text-left text-xs font-normal uppercase"
                                        >
                                            Size
                                        </th>
                                        <th
                                            class="font-avenir-medium px-6 py-4 text-left text-xs font-normal uppercase"
                                        >
                                            Lead Time
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(
                                            item, itemIndex
                                        ) in availability.items"
                                        :key="item.id"
                                        class="border-b last:border-b-0"
                                    >
                                        <td
                                            class="px-6 py-3"
                                            :class="{
                                                'bg-grind-50':
                                                    itemIndex % 2 === 0,
                                                'rounded-bl-xl':
                                                    itemIndex ===
                                                    availability.items.length -
                                                        1,
                                            }"
                                        >
                                            {{ item.description }}
                                        </td>
                                        <td
                                            v-if="
                                                item.turnaround_min ||
                                                item.turnaround_max
                                            "
                                            class="px-6 py-3"
                                            :class="{
                                                'bg-grind-50':
                                                    itemIndex % 2 === 0,
                                            }"
                                        >
                                            {{ item.turnaround_min }}–{{
                                                item.turnaround_max
                                            }}
                                            business days
                                        </td>
                                        <td
                                            class="px-6 py-3"
                                            :class="{
                                                'bg-grind-50':
                                                    itemIndex % 2 === 0,
                                                'rounded-br-xl':
                                                    itemIndex ===
                                                    availability.items.length -
                                                        1,
                                            }"
                                        >
                                            <a
                                                :href="item.link_url"
                                                target="_blank"
                                                >{{ item.link_text }}</a
                                            >
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <hr v-if="groupIndex !== productType.groups.length - 1" />
            </div>

            <hr
                v-if="productTypeIndex !== productTypes.length - 1"
                class="mb-20"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { AlgoliaProductTurnaround } from '~/types/LeadTimes';

const { data: leadTimesData } = await useFetch(`/nuxt-api/lead-times`);

const props = defineProps<{
    items: Array<AlgoliaProductTurnaround>;
}>();

const productTypes = computed(() => {
    if (
        !leadTimesData.value ||
        leadTimesData.value.groups === undefined ||
        leadTimesData.value.availabilities === undefined
    ) {
        return [];
    }

    // Step 1: Sort items into groups

    const groups = [...leadTimesData.value.groups];

    groups.forEach((group) => {
        group.items = [];
    });

    props.items.forEach((item) => {
        let groupFound = false;

        groups.forEach((group) => {
            if (!groupFound && group.name == item.group) {
                group.items.push(item);
                groupFound = true;
            }
        });
    });

    // Step 2: Sort group items into availabilities

    groups.forEach((group) => {
        group.availabilities = [];

        if (group.items.length === 0) {
            return;
        }

        const availabilities = [...leadTimesData.value.availabilities];

        availabilities.forEach((availability) => {
            availability.items = [];

            group.items.forEach((item) => {
                if (item.category === availability.name) {
                    availability.items.push(item);
                }
            });

            if (availability.items.length > 0) {
                group.availabilities.push({ ...availability });
            }
        });

        delete group.items;
    });

    // Step 3: Sort groups into product types

    const productTypes = [];

    groups.forEach((group) => {
        if (group.availabilities.length === 0) {
            return;
        }

        let productTypeExists = false;

        productTypes.forEach((productType) => {
            if (!productTypeExists && productType.name == group.productType) {
                productType.groups.push(group);
                productTypeExists = true;
            }
        });

        if (!productTypeExists) {
            productTypes.push({
                name: group.productType,
                groups: [group],
            });
        }
    });

    productTypes.sort((a, b) => {
        if (a.name === 'Accessories') return 1;
        if (b.name === 'Accessories') return -1;
        return a.name.localeCompare(b.name);
    });

    return productTypes;
});

const singular = (name: string) => {
    // for "accessories"
    if (name.endsWith('ies')) {
        return name.slice(0, -3) + 'y';
    } else if (name.endsWith('ches')) {
        // for "pouches"
        return name.slice(0, -2);
    } else if (name.endsWith('s')) {
        return name.slice(0, -1);
    }
    return name;
};
</script>
